/**
 * API-Client für IONOS Backend (PHP + MySQL)
 */
import { API_BASE_URL } from './config.js';


function getCompanyId() {
  if (typeof localStorage !== 'undefined') {
    const id = localStorage.getItem('devsuite_company_id');
    if (id && !isNaN(Number(id))) return Number(id);
  }
  return null;
}

function url(path, params = {}) {
  const base = API_BASE_URL.replace(/\/$/, '');
  const companyId = getCompanyId();
  const urlObj = new URL(`${base}/${path.replace(/^\//, '')}`, window.location.origin);
  if (companyId && (path.endsWith('.php') || path.includes('.php?'))) {
    urlObj.searchParams.set('company_id', companyId);
  }
  // Weitere Parameter anhängen
  Object.entries(params).forEach(([k, v]) => urlObj.searchParams.set(k, v));
  return urlObj.toString();
}

async function request(path, options = {}) {
  let params = {};
  let method = (options.method || 'GET').toUpperCase();
  let body = options.body;
  // company_id für GET/DELETE als Query, für POST/PUT im Body
  if (method === 'GET' || method === 'DELETE') {
    // company_id wird in url() automatisch angehängt
  } else if (method === 'POST' || method === 'PUT') {
    try {
      const companyId = getCompanyId();
      if (companyId) {
        let data = body ? JSON.parse(body) : {};
        if (!data.company_id) {
          data.company_id = companyId;
          body = JSON.stringify(data);
        }
      }
    } catch { }
  }
  const fullUrl = url(path, params);
  let res;
  try {
    res = await fetch(fullUrl, {
      ...options,
      body,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // Cookies (Session-ID) mitsenden
    });
  } catch (err) {
    const msg = err?.message || '';
    if (msg.includes('fetch') || msg.includes('NetworkError') || msg.includes('Load failed')) {
      const base = fullUrl.replace(/\/[^/]+\.php.*$/, '');
      throw new Error(
        `Netzwerkfehler: API nicht erreichbar (${fullUrl}). ` +
        `Bei lokaler Entwicklung (localhost): Dev-Server neu starten (npm run dev), dann werden Anfragen per Proxy umgeleitet. ` +
        `Prüfen Sie sonst die Internetverbindung und VITE_API_URL in .env. Test im Browser: ${base}/health.php`
      );
    }
    throw err;
  }
  let text = await res.text();
  if (text && typeof text === 'string' && text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  text = text ? text.trim() : '';
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`API Fehler: ${res.status} ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
  }
  return data;
}

// --- Verbindungstest (MySQL auf IONOS) ---
/** Prüft ob die API und die MySQL-Datenbank erreichbar sind. Returns { status: 'ok'|'error', message } */
export async function apiHealthCheck() {
  const base = API_BASE_URL.replace(/\/$/, '');
  const res = await fetch(`${base}/health.php`, { headers: { Accept: 'application/json' } });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    return { status: 'error', message: `API nicht erreichbar: ${res.status} ${text.slice(0, 100)}` };
  }
  if (!res.ok) {
    return { status: 'error', message: data?.message || data?.error || `HTTP ${res.status}` };
  }
  return { status: data?.status || 'ok', message: data?.message || '', details: data?.details };
}

// --- Gesendete E-Mails (Versand-Log) ---
export async function apiGetSentEmails() {
  return request('sent-emails.php');
}

// --- Company Settings ---
export async function apiGetSettings() {
  return request('settings.php');
}

export async function apiSaveSettings(settings) {
  return request('settings.php', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

// --- Customers ---
export async function apiGetCustomers() {
  return request('customers.php');
}

export async function apiCreateCustomer(customer) {
  const data = await request('customers.php', {
    method: 'POST',
    body: JSON.stringify(customer),
  });
  return data.id;
}

export async function apiUpdateCustomer(customer) {
  return request('customers.php', {
    method: 'PUT',
    body: JSON.stringify(customer),
  });
}

export async function apiDeleteCustomer(id) {
  return request(`customers.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// --- Invoices ---
export async function apiGetInvoices() {
  return request('invoices.php');
}

/** Nächste fortlaufende Rechnungsnummer (z. B. { id: 'INV-000001' }). */
export async function apiGetNextInvoiceNumber() {
  return request('next-invoice-number.php');
}

export async function apiCreateInvoice(invoice) {
  return request('invoices.php', {
    method: 'POST',
    body: JSON.stringify(invoice),
  });
}

export async function apiUpdateInvoice(invoice) {
  return request('invoices.php', {
    method: 'PUT',
    body: JSON.stringify(invoice),
  });
}

export async function apiDeleteInvoice(id) {
  return request(`invoices.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** PDF-Blob der Rechnung auf Server hochladen (multipart). */
export async function apiUploadInvoicePdf(invoiceId, pdfBlob) {
  const formData = new FormData();
  formData.append('invoice_id', invoiceId);
  formData.append('pdf', pdfBlob, `${invoiceId}.pdf`);
  const base = API_BASE_URL.replace(/\/$/, '');
  const res = await fetch(`${base}/upload-pdf.php`, {
    method: 'POST',
    body: formData,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`API: ${res.status} ${text.slice(0, 100)}`);
  }
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

/** Rechnung per E-Mail versenden (mit PDF-Anhang). Nutzt health.php?action=invoice_email (funktioniert auch wenn invoice-email.php 500 liefert). */
export async function apiSendInvoiceEmail({ invoiceId, to, subject, body }) {
  return request('health.php?action=invoice_email', {
    method: 'POST',
    body: JSON.stringify({ invoice_id: invoiceId, to, subject, body }),
  });
}

/** Angebot per E-Mail versenden (mit PDF-Anhang). Nutzt health.php?action=quote_email (funktioniert auch wenn quote-email.php 500 liefert). */
export async function apiSendQuoteEmail({ quoteId, to, subject, body }) {
  return request('health.php?action=quote_email', {
    method: 'POST',
    body: JSON.stringify({ quote_id: quoteId, to, subject, body }),
  });
}

/** PDF-Blob des Angebots auf Server hochladen (multipart). */
export async function apiUploadQuotePdf(quoteId, pdfBlob) {
  const formData = new FormData();
  formData.append('quote_id', quoteId);
  formData.append('pdf', pdfBlob, `${quoteId}.pdf`);
  const base = API_BASE_URL.replace(/\/$/, '');
  const res = await fetch(`${base}/upload-quote-pdf.php`, {
    method: 'POST',
    body: formData,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`API: ${res.status} ${text.slice(0, 100)}`);
  }
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

/** Firmenlogo hochladen (Bilddatei). Gibt die volle Logo-URL zurück. */
export async function apiUploadLogo(file) {
  const base = API_BASE_URL.replace(/\/$/, '');
  const formData = new FormData();
  formData.append('logo', file, file.name || 'logo');
  let res;
  try {
    res = await fetch(`${base}/upload-logo.php`, {
      method: 'POST',
      body: formData,
    });
  } catch (e) {
    throw new Error(
      'Server nicht erreichbar (Failed to fetch). Prüfen Sie VITE_API_URL in .env und ob upload-logo.php auf dem Server liegt.'
    );
  }
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`Ungültige Antwort vom Server: ${text.slice(0, 80)}`);
  }
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  const path = data?.path || '';
  return path ? `${base}/${path.replace(/^\//, '')}` : '';
}

// --- Quotes ---
export async function apiGetQuotes() {
  return request('quotes.php');
}

/** Nächste fortlaufende Angebotsnummer (z. B. { id: 'ANG-000001' }). */
export async function apiGetNextQuoteNumber() {
  return request('next-quote-number.php');
}

export async function apiCreateQuote(quote) {
  return request('quotes.php', {
    method: 'POST',
    body: JSON.stringify(quote),
  });
}

export async function apiUpdateQuote(quote) {
  return request('quotes.php', {
    method: 'PUT',
    body: JSON.stringify(quote),
  });
}

export async function apiDeleteQuote(id) {
  return request(`quotes.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// --- Subscriptions ---
export async function apiGetSubscriptions() {
  return request('subscriptions.php');
}

export async function apiCreateSubscription(sub) {
  return request('subscriptions.php', {
    method: 'POST',
    body: JSON.stringify(sub),
  });
}

export async function apiUpdateSubscription(sub) {
  return request('subscriptions.php', {
    method: 'PUT',
    body: JSON.stringify(sub),
  });
}

export async function apiDeleteSubscription(id) {
  return request(`subscriptions.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function apiRunAutomation() {
  return request('run-automation.php', { method: 'POST', body: JSON.stringify({}) });
}

// --- Customer Notes ---
export async function apiGetCustomerNotes(customerId) {
  return request(`customer-notes.php?customer_id=${encodeURIComponent(customerId)}`);
}
export async function apiCreateCustomerNote(customerId, content) {
  return request('customer-notes.php', { method: 'POST', body: JSON.stringify({ customerId, content }) });
}
export async function apiDeleteCustomerNote(id) {
  return request(`customer-notes.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// --- Customer Documents ---
export async function apiGetCustomerDocuments(customerId) {
  return request(`customer-documents.php?customer_id=${encodeURIComponent(customerId)}`);
}
export async function apiUploadCustomerDocument(customerId, file, title = '', category = '', folder = '') {
  const base = API_BASE_URL.replace(/\/$/, '');
  const formData = new FormData();
  formData.append('customer_id', String(customerId));
  formData.append('title', title || '');
  formData.append('category', category || '');
  formData.append('folder', folder || '');
  formData.append('file', file, file?.name || 'dokument');
  const companyId = getCompanyId();
  const endpoint = companyId
    ? `${base}/customer-documents.php?company_id=${encodeURIComponent(companyId)}`
    : `${base}/customer-documents.php`;
  const res = await fetch(endpoint, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`API: ${res.status} ${text.slice(0, 120)}`);
  }
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}
export async function apiDeleteCustomerDocument(id) {
  return request(`customer-documents.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function apiSearchAll(query) {
  return request(`search.php?q=${encodeURIComponent(query || '')}`);
}

// --- Contact Activities ---
export async function apiGetContactActivities(customerId) {
  return request(`contact-activities.php?customer_id=${encodeURIComponent(customerId)}`);
}
export async function apiCreateContactActivity(data) {
  return request('contact-activities.php', { method: 'POST', body: JSON.stringify(data) });
}
export async function apiDeleteContactActivity(id) {
  return request(`contact-activities.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// --- Tasks ---
export async function apiGetTasks(params = {}) {
  const q = new URLSearchParams();
  if (params.customerId != null) q.set('customer_id', params.customerId);
  if (params.completed != null) q.set('completed', params.completed);
  const suffix = q.toString() ? '?' + q.toString() : '';
  return request('tasks.php' + suffix);
}
export async function apiCreateTask(data) {
  return request('tasks.php', { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateTask(data) {
  return request('tasks.php', { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteTask(id) {
  return request(`tasks.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}
// --- Admin API ---
export async function apiGetFirmen() {
  return request('companies.php');
}
export async function apiAddFirma(data) {
  return request('companies.php', { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateFirma(data) {
  return request('companies.php', { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteFirma(id) {
  return request(`companies.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export async function apiGetNutzer() {
  return request('users.php');
}
export async function apiAddNutzer(data) {
  return request('users.php', { method: 'POST', body: JSON.stringify(data) });
}
export async function apiUpdateNutzer(data) {
  return request('users.php', { method: 'PUT', body: JSON.stringify(data) });
}
export async function apiDeleteNutzer(id) {
  return request(`users.php?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}
export async function apiAdminOverview() {
  return request('admin-overview.php');
}
export async function apiAdminGetCompanyConfig(companyId) {
  return request(`admin-company-config.php?company_id=${encodeURIComponent(companyId)}`);
}
export async function apiAdminSaveCompanyConfig(payload) {
  return request('admin-company-config.php', { method: 'PUT', body: JSON.stringify(payload) });
}
export async function apiAdminGetAudit(limit = 100, companyId = null) {
  const q = new URLSearchParams();
  q.set('limit', String(limit));
  if (companyId != null) q.set('company_id', String(companyId));
  return request(`admin-audit.php?${q.toString()}`);
}
export async function apiAdminAddAuditNote(action, targetType = 'system', targetId = null, meta = {}) {
  return request('admin-audit.php', {
    method: 'POST',
    body: JSON.stringify({ action, target_type: targetType, target_id: targetId, meta }),
  });
}
export async function apiAdminMaintenanceStatus() {
  return request('admin-maintenance.php');
}
export async function apiAdminMaintenanceAction(action) {
  return request('admin-maintenance.php', { method: 'POST', body: JSON.stringify({ action }) });
}
export async function apiAdminMonitoring() {
  return request('admin-monitoring.php');
}

// --- Profile (Self-Service) ---
export async function apiUpdateProfile(data) {
  return request('profile.php', { method: 'PUT', body: JSON.stringify(data) });
}
